<template>
  <section ref="root" class="trend-stack" :aria-label="title"
    @pointerenter="enterGroup" @pointermove="inspectPoint" @pointerdown="pinPoint" @pointerleave="leaveGroup" @pointercancel="leaveGroup">
    <header class="trend-stack-heading">
      <span class="inspect-date">{{ selectedDate && inspecting ? fullDate(selectedDate) : endDate ? `Latest · ${fullDate(endDate)}` : 'Date unavailable' }}</span>
      <label>Period
        <select v-model.number="days" :aria-label="`${title} period`">
          <option :value="30">Last 30 days</option><option :value="7">Last 7 days</option><option :value="90">Last 90 days</option>
        </select>
      </label>
    </header>
    <p v-if="loading" class="trend-status">Loading history…</p>
    <p v-else-if="error" class="trend-status">History unavailable. <button type="button" @click="$emit('retry')">Retry</button></p>
    <template v-else>
      <div ref="chartGroup" class="trend-rows" role="group" tabindex="0" :aria-label="`${title}. Use left and right arrow keys to explore saved dates, Home and End to jump, and Escape to clear.`"
        :aria-describedby="summaryId" @focus="focusGroup" @blur="blurGroup" @keydown.left.prevent="movePoint(-1)" @keydown.right.prevent="movePoint(1)"
        @keydown.home.prevent="selectPoint(0)" @keydown.end.prevent="selectPoint(savedDates.length - 1)" @keydown.esc.stop.prevent="dismiss">
        <div v-for="series in charts" :key="series.metric" class="metric-row" :data-metric="series.metric" :style="{ '--trend-line': series.color }">
          <div class="metric-info">
            <h3>{{ series.label }}</h3>
            <strong class="metric-value">{{ formatValue(series.metric, series.displayValue) }}</strong>
            <span class="metric-change" :title="series.comparisonTitle">{{ series.changeLabel }}</span>
          </div>
          <svg class="metric-chart" :viewBox="`0 0 ${width} 56`" role="img" :aria-label="`${series.label}: ${series.validPoints.length} comparable saved days. ${series.definition}`">
            <line class="axis" :x1="leftInset" :x2="width - 4" y1="48" y2="48" />
            <g v-if="inspecting && series.validPoints.length" class="scale" aria-hidden="true">
              <g v-for="tick in series.ticks" :key="tick.value">
                <line :x1="leftInset" :x2="width - 4" :y1="tick.y" :y2="tick.y" />
                <text :x="leftInset - 5" :y="tick.y" text-anchor="end" dominant-baseline="middle">{{ tick.label }}</text>
              </g>
            </g>
            <path v-for="(path, index) in series.gaps" :key="`gap-${index}`" class="gap" :d="path" />
            <path v-for="(path, index) in series.paths" :key="`path-${index}`" class="series" :d="path" />
            <circle v-for="point in series.plotted" :key="point.date" class="saved-point" :cx="point.x" :cy="point.y" r="0.75" />
            <g v-if="inspecting && series.selected" class="inspection" aria-hidden="true">
              <line :x1="series.selected.x" :x2="series.selected.x" y1="8" y2="48" />
              <circle v-if="series.selected.y != null" :cx="series.selected.x" :cy="series.selected.y" r="2" />
            </g>
            <text v-if="!series.validPoints.length" class="empty-chart" :x="width / 2" y="28" text-anchor="middle">No comparable history</text>
          </svg>
        </div>
      </div>
      <div v-if="startDate && endDate" class="shared-dates" :style="{ paddingLeft: `${leftInset}px` }" aria-hidden="true"><span>{{ shortDate(startDate) }}</span><span>{{ shortDate(endDate) }}</span></div>
      <span :id="summaryId" class="visually-hidden" :aria-live="focused ? 'polite' : 'off'">{{ inspectionSummary }}</span>
    </template>
    <p class="definition">Shared dates, individual value scales. {{ definition }}</p>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { buildDashboardTrend, isRateMetric } from './dashboardTrendData';
import { fmtInt, fmtPct } from '../../../../dashboard/public/js/format.js';

const props = defineProps({ snapshot: { type: Object, required: true }, rows: { type: Array, default: () => [] }, loading: Boolean, error: String,
  title: { type: String, required: true }, definition: String, metrics: { type: Array, required: true }, period: Number });
const emit = defineEmits(['retry', 'update:period']);
const root = ref(null);
const chartGroup = ref(null);
const localDays = ref(7);
const days = computed({
  get: () => props.period ?? localDays.value,
  set: value => { localDays.value = value; emit('update:period', value); }
});
const width = ref(320);
const hovered = ref(false), focused = ref(false), pinned = ref(false), dismissed = ref(false);
const selectedDate = ref(null);
const summaryId = `trend-stack-summary-${getCurrentInstance().uid}`;
const inspecting = computed(() => !props.loading && !props.error && !dismissed.value && (hovered.value || focused.value || pinned.value));
const formatValue = (metric, value) => value == null ? '—' : isRateMetric(metric) ? fmtPct(value) : fmtInt(value);
const dateFormat = options => date => new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
const shortDate = dateFormat({ month: 'short', day: 'numeric' });
const fullDate = dateFormat({ month: 'short', day: 'numeric', year: 'numeric' });
const compactCount = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const formatScale = (metric, value) => isRateMetric(metric) ? fmtPct(value) : value >= 10000 ? compactCount.format(value) : fmtInt(value);
const series = computed(() => props.metrics.map(metric => {
  const trend = buildDashboardTrend({ rows: props.rows, metric: metric.metric, value: metric.value,
    generatedAt: props.snapshot.generatedAt, snapshotVersion: props.snapshot.snapshotVersion, days: days.value });
  const validPoints = trend.points.filter(point => point.value != null);
  const isRate = isRateMetric(metric.metric);
  const values = validPoints.map(point => point.value);
  const low = values.length ? Math.min(...values) : 0, high = values.length ? Math.max(...values) : isRate ? 1 : 10;
  // Match the headline charts' minimum range, so small changes stay in proportion.
  const span = Math.max(high - low, isRate ? .08 : Math.max(10, high * .1));
  const power = 10 ** Math.floor(Math.log10(span / 2));
  const step = [1, 2, 5, 10].find(value => value * power >= span / 2) * power;
  let min = Math.max(0, Math.floor(low / step) * step);
  let max = Math.max(min + 2 * step, Math.ceil(high / step) * step);
  if (isRate && max > 1) { max = 1; min = Math.max(0, Math.min(min, max - 2 * step)); }
  const ticks = [max, min].map((value, index) => ({ value, y: 8 + index * 40, label: formatScale(metric.metric, value) }));
  return { ...metric, trend, validPoints, min, max, ticks };
}));
const startDate = computed(() => series.value[0]?.trend.startDate);
const endDate = computed(() => series.value[0]?.trend.endDate);
// All rows share the same plot bounds, including when their scale labels differ in width.
const leftInset = computed(() => inspecting.value
  ? Math.min(width.value * .45, Math.max(32, ...series.value.flatMap(item => item.ticks.map(tick => tick.label.length * 6.5 + 5)))) : 4);
const savedDates = computed(() => [...new Set(series.value.flatMap(item => item.validPoints.map(point => point.date)))].sort());
const charts = computed(() => series.value.map(item => {
  const coordinates = item.trend.points.map((point, index) => ({ ...point,
    x: leftInset.value + index / days.value * (width.value - leftInset.value - 4),
    y: point.value == null ? null : 48 - (point.value - item.min) / (item.max - item.min) * 40
  }));
  const paths = [], gaps = [];
  let run = [], previous = null, gap = false;
  const coordinate = point => `${point.x.toFixed(2)},${point.y.toFixed(2)}`;
  const finish = () => { if (run.length > 1) paths.push(run.map((point, index) => `${index ? 'L' : 'M'}${coordinate(point)}`).join(' ')); run = []; };
  for (const point of coordinates) {
    if (point.y == null) { gap = true; finish(); continue; }
    if (gap) gaps.push(`M${coordinate(previous || { x: leftInset.value, y: point.y })} L${coordinate(point)}`);
    previous = point; gap = false; run.push(point);
  }
  finish();
  const selected = coordinates.find(point => point.date === selectedDate.value);
  const displayPoint = inspecting.value && selected ? selected : coordinates.at(-1);
  const displayValue = displayPoint?.value ?? null;
  const baseline = displayValue != null ? item.validPoints.find(point => point.date < displayPoint.date) : null;
  const change = baseline ? displayValue - baseline.value : null;
  const duration = baseline ? Math.round((Date.parse(displayPoint.date) - Date.parse(baseline.date)) / 86400000) : null;
  const magnitude = Math.abs(change);
  const amount = isRateMetric(item.metric) ? `${Number((magnitude * 100).toFixed(1)) === 0 ? '<0.1' : (magnitude * 100).toFixed(1)} pp` : fmtInt(magnitude);
  const changeLabel = change == null ? displayValue == null ? 'Value unavailable' : 'No earlier history'
    : `${change === 0 ? 'no change' : `${change > 0 ? '↑' : '↓'} ${amount}`} · ${duration} ${duration === 1 ? 'day' : 'days'}`;
  const comparisonTitle = baseline ? `Compared with ${fullDate(baseline.date)}. Selected window: ${days.value} days.` : 'No earlier comparable snapshot in this period.';
  return { ...item, paths, gaps, selected, plotted: coordinates.filter(point => point.y != null), displayValue, changeLabel, comparisonTitle };
}));
const inspectionSummary = computed(() => `${selectedDate.value && inspecting.value ? fullDate(selectedDate.value) : endDate.value ? fullDate(endDate.value) : 'Date unavailable'}. ${charts.value.map(item => `${item.label}: ${formatValue(item.metric, item.displayValue)}. ${item.changeLabel.replace('pp', 'percentage points')}`).join('. ')}`);

function inspectPoint(event) {
  if (props.loading || props.error || !savedDates.value.length) return;
  const rect = root.value?.querySelector('.metric-chart')?.getBoundingClientRect();
  if (!rect?.width || !Number.isFinite(event.clientX)) return;
  const x = (event.clientX - rect.left) * width.value / rect.width;
  const index = Math.max(0, Math.min(days.value, (x - leftInset.value) / (width.value - leftInset.value - 4) * days.value));
  const time = Date.parse(startDate.value) + index * 86400000;
  // Snap to actual saved dates, never to interpolated guide values. A missing
  // metric at that date stays unavailable rather than borrowing another day.
  selectedDate.value = savedDates.value.reduce((best, date) => Math.abs(Date.parse(date) - time) < Math.abs(Date.parse(best) - time) ? date : best);
}
function enterGroup(event) {
  hovered.value = event.pointerType !== 'touch';
  dismissed.value = false;
  inspectPoint(event);
}
function leaveGroup() {
  hovered.value = false;
  if (!focused.value && !pinned.value) selectedDate.value = null;
}
function pinPoint(event) {
  if (event.pointerType !== 'touch' || event.target.closest('select, button')) return;
  chartGroup.value?.focus({ preventScroll: true });
  pinned.value = true;
  dismissed.value = false;
  inspectPoint(event);
}
function focusGroup() {
  focused.value = true;
  dismissed.value = false;
  if (!selectedDate.value) selectPoint(savedDates.value.length - 1);
}
function blurGroup() {
  focused.value = false;
  pinned.value = false;
  if (!hovered.value) selectedDate.value = null;
}
function dismiss() { dismissed.value = true; pinned.value = false; selectedDate.value = null; }
function selectPoint(index) {
  if (!savedDates.value.length) return;
  selectedDate.value = savedDates.value[Math.max(0, Math.min(savedDates.value.length - 1, index))];
  dismissed.value = false;
}
function movePoint(direction) {
  const index = savedDates.value.indexOf(selectedDate.value);
  selectPoint((index < 0 ? savedDates.value.length - 1 : index) + direction);
}
watch([days, () => props.title, () => props.snapshot, () => props.loading, () => props.error], () => {
  selectedDate.value = null; pinned.value = false;
  if (props.loading || props.error) { hovered.value = false; focused.value = false; }
});
let observer;
onMounted(() => {
  if (typeof ResizeObserver === 'undefined') return;
  observer = new ResizeObserver(() => {
    const chartWidth = root.value?.querySelector('.metric-chart')?.getBoundingClientRect().width;
    if (chartWidth > 0) width.value = chartWidth;
  });
  observer.observe(root.value);
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.trend-stack { --label-width: 140px; --column-gap: 18px; color: var(--text-primary); }
.trend-stack-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.inspect-date { color: var(--text-secondary); font-size: 12px; font-variant-numeric: tabular-nums; }
label { display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--text-secondary); }
select { padding: 5px 7px; border: 1px solid var(--border); border-radius: 6px; background: var(--surface); color: var(--text-primary); font: inherit; }
.trend-rows { border-radius: 4px; }
.trend-rows:focus-visible, select:focus-visible { outline: 2px solid var(--blue); outline-offset: 3px; }
.metric-row { display: grid; grid-template-columns: var(--label-width) minmax(0, 1fr); gap: var(--column-gap); align-items: center; padding: 12px 0; border-bottom: 1px solid var(--border); }
.metric-info { display: grid; gap: 2px; min-width: 0; }
h3 { font-size: 12px; line-height: 1.4; font-weight: 400; margin: 0; }
.metric-value { font-size: 20px; line-height: 1.3; font-weight: 500; font-variant-numeric: tabular-nums; }
.metric-change { font-size: 11px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.metric-chart { width: 100%; height: 56px; display: block; overflow: visible; }
.series { fill: none; stroke: var(--trend-line); stroke-width: 1.8; stroke-linejoin: round; stroke-linecap: round; }
.saved-point { fill: var(--trend-line); }
.axis, .scale line { stroke: var(--border, #e8e6e3); stroke-width: 1; }
.scale line { stroke-dasharray: 2 3; }
.scale text, .empty-chart { fill: var(--text-tertiary, #767676); font: 10px system-ui, sans-serif; }
.gap, .inspection line { fill: none; stroke: var(--text-tertiary, #767676); stroke-width: 1; stroke-dasharray: 2 4; opacity: .6; }
.inspection circle { fill: var(--surface, white); stroke: var(--trend-line); stroke-width: 1.5; }
.shared-dates { margin-left: calc(var(--label-width) + var(--column-gap)); padding-right: 4px; display: flex; justify-content: space-between; gap: 6px; padding-top: 6px; color: var(--text-tertiary); font-size: 11px; }
.definition, .trend-status { color: var(--text-secondary); font-size: 12px; line-height: 1.6; margin: 20px 0 0; }
.trend-status button { border: 0; background: transparent; color: var(--blue); padding: 0; text-decoration: underline; }
@media (max-width: 430px) { .trend-stack { --label-width: 112px; --column-gap: 12px; } }
@media (pointer: coarse) { select { min-height: 44px; font-size: 16px; } }
</style>
